import { FC, ReactElement } from 'react'

import { convertDateToDateYearCompleteString, convertDateToTime } from '@/helpers/DateTimeParser'
import { trans } from '@/helpers/trans'
import { LogChange } from '@/models'

import { TrackLogItem } from './TrackLogItem'

import './TrackLog.scss'

const generateTimeLog = (start: LogChange, end: LogChange, translation: string): string | null => {
    if (! start.oldValue || ! start.newValue || ! end.oldValue || ! end.newValue) {
        return null
    }

    return trans(translation, {
        start1: convertDateToTime(start.oldValue),
        start2: convertDateToTime(start.newValue),
        end1: convertDateToTime(end.oldValue),
        end2: convertDateToTime(end.newValue),
    })
}

const generatePauseLog = (pause: LogChange): string | null => {
    if (! pause.oldValue || ! pause.newValue) {
        return null
    }

    return trans('pages.logs.pause', {
        pause1: pause.oldValue,
        pause2: pause.newValue,
    })
}

const generateMealLog = (meal: LogChange): string | null => {
    if (! meal.oldValue || ! meal.newValue) {
        return null
    }

    return trans('pages.logs.meal', {
        meal1: trans(`pages.tracks.${meal.oldValue.toLowerCase()}`),
        meal2: trans(`pages.tracks.${meal.newValue.toLowerCase()}`),
    })
}

interface TrackLogProps {
    actor: string
    loggedAt: string
    checkIn?: LogChange
    checkOut?: LogChange
    billableStart?: LogChange
    billableEnd?: LogChange
    pause?: LogChange
    meal?: LogChange
}

export const TrackLog: FC<TrackLogProps> = ({
    actor,
    loggedAt,
    checkIn = {},
    checkOut = {},
    billableStart = {},
    billableEnd = {},
    pause = {},
    meal = {},
}): ReactElement | null => {
    const checkInOutLog = generateTimeLog(checkIn, checkOut, 'pages.logs.checkInOut')
    const billableStartEnd = generateTimeLog(billableStart, billableEnd, 'pages.logs.billableStartEnd')
    const pauseLog = generatePauseLog(pause)
    const mealLog = generateMealLog(meal)
    const dateTimeString = `${convertDateToDateYearCompleteString(loggedAt)} ${convertDateToTime(loggedAt)}`

    if (! checkInOutLog && ! billableStartEnd && ! pauseLog && ! mealLog) {
        return null
    }

    return (
        <li className="list-group-item-medium list-group-item track-log">
            <div className="track-log__actor">
                {trans('pages.logs.changedBy', { actor, date: dateTimeString })}
            </div>
            <ul className="track-log__list">
                {checkInOutLog && (
                    <TrackLogItem
                        title={trans('pages.shifts.clockedTime')}
                        text={checkInOutLog}
                    />
                )}
                {billableStartEnd && (
                    <TrackLogItem
                        title={trans('pages.tracks.billableTime')}
                        text={billableStartEnd}
                    />
                )}
                {pauseLog && (
                    <TrackLogItem
                        title={trans('common.break')}
                        text={pauseLog}
                    />
                )}
                {mealLog && (
                    <TrackLogItem
                        title={trans('common.meal')}
                        text={mealLog}
                    />
                )}
            </ul>
        </li>
    )
}
